import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./competenciesV2.html";
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { WireStore } from '@oneappexchange/lwc-wired';
import { routerStore, selectScrollToQueryParam } from '~/modules/stores/routerStore';
import { scrollToElement } from '~/modules/utilities/browser';
import { images } from '~/modules/utilities/assetUtilities';
/**
 * v2 Competencies panel — embedded by `pages/listingDetailV2` in place of v1's
 * `listing-competencies`.
 *
 * This began as a STANDALONE WHOLESALE COPY of the former v1
 * `listing/competencies` (not a subclass). While v1's `listing-competencies` was
 * still shared by the v1 `pages/listingDetail` page under a zero-regression bar,
 * we did NOT edit it or make v2 inherit from it: copying kept v1 untouched, let
 * the v2 panel evolve its own responsive layout independently, and made
 * retirement a pure delete. v1 `listing/competencies` has since been deleted at
 * 100% rollout (W-24557579), so this is now the sole Competencies implementation.
 * Mirrors the `overview` -> `overviewV2` fork recipe; see
 * `docs/listing-detail-v2.md`.
 *
 * The TS class carried v1's behavior over verbatim (tabs, tooltips,
 * expand/collapse, certifications modal, formatting) — the ONLY v2 divergences
 * live in the co-located `.html`/`.css`: at tablet/mobile widths the horizontal
 * 5-column table collapses into stacked per-category cards (Figma nodes
 * 7871-49281 tablet / 7627-99903 mobile). The Industry/Product toggle keeps its
 * desktop styling at every breakpoint. Desktop has no v2 design and keeps v1's
 * table. See `docs/W-23636627-competenciesv2-changes.md`.
 */

const TAB = {
  INDUSTRY: 'Industry',
  PRODUCT: 'Product'
};

// View model for a single toggle button in the Industry/Product switcher.

// View model types for display purposes

class CompetenciesV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.i18n = void 0;
    this.scrollToParam = void 0;
    this.expandedRowsByTab = {
      [TAB.INDUSTRY]: {},
      [TAB.PRODUCT]: {}
    };
    // Null until the user switches tabs; until then activeTab falls back to
    // defaultTab so the populated category is selected first.
    this._activeTab = null;
    /**
     * Custom tooltip implementation instead of wds-popover-tooltip
     *
     * Why custom? The table has overflow-x: auto for horizontal scrolling.
     * The wds-popover-tooltip uses position: absolute, which gets clipped by
     * the table's overflow container. Our custom tooltip uses position: fixed
     * to escape the overflow clipping and remain visible above all content.
     */
    this.openTooltip = null;
    this.openTooltipTrigger = null;
    this.tooltipRafId = null;
    this.focusRafId = null;
    this.handleOutsideClick = this.onOutsideClick.bind(this);
    this.handleEscapeKey = this.onEscapeKey.bind(this);
    this.handleResize = this.onResize.bind(this);
    this.handleScroll = this.onScroll.bind(this);
    // Modal state
    this.showCertificationsModal = false;
    this.showMetricsModal = false;
    // The buttons that opened each modal. Stored so focus can be returned to
    // them on close — the modals are unmounted (lwc:if) rather than hidden, so
    // without this focus would fall to <body> (W-23850362).
    this.certificationsModalTrigger = null;
    this.metricsModalTrigger = null;
    this.modalCompetencyName = '';
    this.modalSubcategoryName = '';
    this.modalCertifications = [];
    this.modalTotalCertifications = 0;
  }
  get listing() {
    return this._listing;
  }
  set listing(value) {
    this._listing = value;
  }
  get activeTab() {
    return this._activeTab ?? this.defaultTab;
  }
  connectedCallback() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleEscapeKey);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll, {
      passive: true
    });
  }
  renderedCallback() {
    scrollToElement(this, this.scrollToParam);
  }
  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscapeKey);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll, {
      passive: true
    });
    if (this.tooltipRafId !== null) {
      cancelAnimationFrame(this.tooltipRafId);
      this.tooltipRafId = null;
    }
    if (this.focusRafId !== null) {
      cancelAnimationFrame(this.focusRafId);
      this.focusRafId = null;
    }
  }
  onOutsideClick(event) {
    const clickedElement = event.target;
    if (!clickedElement) {
      return;
    }

    // Check if click is within this component's host element
    const hostElement = this.template?.host;
    if (!hostElement || clickedElement === hostElement || hostElement.contains?.(clickedElement)) {
      return;
    }
    this.closeAllTooltips();
  }
  onEscapeKey(event) {
    if (event.key === 'Escape' && this.openTooltip) {
      this.closeAllTooltips(true);
    }
  }
  get consultantCompetencies() {
    return this.listing?.consultantCompetencies || null;
  }
  get consultantCertifications() {
    return this.listing?.consultantCertifications || null;
  }
  get hasCompetencies() {
    // Read raw data, not the `competencies` view models — this only needs
    // a count, not built rows.
    return !!this.consultantCompetencies?.competencies?.length;
  }
  get errorEmptyStateImg() {
    return images.errorEmptyState;
  }
  get competencies() {
    if (!this.consultantCompetencies?.competencies) {
      return [];
    }
    const competencies = this.consultantCompetencies.competencies.map((comp, index) => {
      const id = `${comp.category}-competency-${index}`;
      const hasSpecializations = comp.specializations && comp.specializations.length > 0;
      // Key expanded state by the row's own category, not activeTab —
      // that keeps this getter off the activeTab -> defaultTab path, which
      // would recurse (and scan per row) if read here.
      const isExpanded = !!this.expandedRowsByTab[comp.category]?.[id];
      const awardNameLower = comp.awardName?.toLowerCase() || '';
      const isExpert = awardNameLower.includes('expert');
      const isAccredited = awardNameLower.includes('accredited');
      const hasCompetencyLevel = !!comp.awardName;

      // Use localized competency level labels
      let competencyLevelDisplay = '';
      if (isExpert) {
        competencyLevelDisplay = this.i18n.listingDetails.competencies.levels.expert.name;
      } else if (isAccredited) {
        competencyLevelDisplay = this.i18n.listingDetails.competencies.levels.accredited.name;
      }
      return {
        ...comp,
        id,
        isExpanded,
        hasSpecializations,
        expandIconClass: `expand-icon ${isExpanded ? 'expanded' : 'collapsed'}`,
        isExpert,
        isAccredited,
        hasCompetencyLevel,
        competencyLevelDisplay,
        formattedNumProjects: this.formatNumber(comp.numProjects),
        formattedAvgCsat: this.formatCsat(comp.avgCsat),
        formattedNumCertifications: this.formatNumber(comp.numCertifications),
        certificationsAriaLabel: i18nStore.getState().i18n.listingDetails.competencies.certificationsAriaLabel({
          NUM_CERTIFICATIONS: comp.numCertifications,
          COMPETENCY_NAME: comp.name
        }),
        expandAriaLabel: i18nStore.getState().i18n.listingDetails.competencies.expandAriaLabel({
          COMPETENCY_NAME: comp.name
        }),
        specializations: (comp.specializations || []).map(spec => ({
          ...spec,
          formattedNumProjects: this.formatNumber(spec.numProjects),
          formattedAvgCsat: this.formatCsat(spec.avgCsat),
          formattedNumCertifications: this.formatNumber(spec.numCertifications),
          certificationsAriaLabel: i18nStore.getState().i18n.listingDetails.competencies.specializationCertificationsAriaLabel({
            NUM_CERTIFICATIONS: spec.numCertifications,
            COMPETENCY_NAME: comp.name,
            SPECIALIZATION_NAME: spec.name
          }),
          parentContext: i18nStore.getState().i18n.listingDetails.competencies.specializationParentContext({
            COMPETENCY_NAME: comp.name
          })
        }))
      };
    });
    return competencies;
  }
  get industryCompetencies() {
    return this.competencies.filter(comp => comp.category === TAB.INDUSTRY);
  }
  get productCompetencies() {
    return this.competencies.filter(comp => comp.category === TAB.PRODUCT);
  }

  /**
   * Tab selected by default: the populated category, so a consultant with only
   * Product competencies doesn't open to an empty Industry tab. Industry wins
   * when both exist. Scans raw data (a `.some()`) so it builds no view models.
   */
  get defaultTab() {
    const hasIndustry = !!this.consultantCompetencies?.competencies?.some(comp => comp.category === TAB.INDUSTRY);
    return hasIndustry ? TAB.INDUSTRY : TAB.PRODUCT;
  }
  get activeCompetencies() {
    return this.activeTab === TAB.INDUSTRY ? this.industryCompetencies : this.productCompetencies;
  }
  get hasActiveCompetencies() {
    // Like hasCompetencies: `.some()` on raw data instead of building rows.
    return !!this.consultantCompetencies?.competencies?.some(comp => comp.category === this.activeTab);
  }

  /** Both toggle buttons in fixed Industry, then Product order. Array order =
   *  DOM order, so the ARIA keyboard pattern (arrow/Home/End) matches. */
  get tabs() {
    return [TAB.INDUSTRY, TAB.PRODUCT].map(key => {
      const isActive = this.activeTab === key;
      const isIndustry = key === TAB.INDUSTRY;
      return {
        key,
        id: isIndustry ? 'competencies-tab-industry' : 'competencies-tab-product',
        label: isIndustry ? this.i18n.listingDetails.competencies.industryTab : this.i18n.listingDetails.competencies.productTab,
        tabClass: `tab ${isActive ? 'active' : ''}`,
        tabIndex: isActive ? '0' : '-1',
        isActive,
        testId: `tab-${key}`
      };
    });
  }
  get activeTabId() {
    return this.activeTab === TAB.INDUSTRY ? 'competencies-tab-industry' : 'competencies-tab-product';
  }
  get categoryColumnHeader() {
    // Localized label for the active category, matching the toggle buttons.
    return this.activeTab === TAB.INDUSTRY ? this.i18n.listingDetails.competencies.industryTab : this.i18n.listingDetails.competencies.productTab;
  }

  /** Empty-state heading for the active tab, e.g. "No industry competencies listed". */
  get activeTabEmptyState() {
    return this.activeTab === TAB.INDUSTRY ? this.i18n.listingDetails.competencies.industryEmptyState : this.i18n.listingDetails.competencies.productEmptyState;
  }
  get emptyStateImg() {
    return images.errorBird;
  }
  get showCompetencyLevelTooltip() {
    return this.openTooltip === 'competencyLevel';
  }
  get showRecentProjectsTooltip() {
    return this.openTooltip === 'recentProjects';
  }
  get showAvgCsatTooltip() {
    return this.openTooltip === 'avgCsat';
  }
  get showCertificationsTooltip() {
    return this.openTooltip === 'certifications';
  }
  handleTabSwitch(event) {
    event.preventDefault();
    const button = event.currentTarget;
    // data-tab values in the template must match the TAB constants above.
    const tab = button.dataset.tab;
    this.switchTo(tab);
  }
  handleTabKeydown(event) {
    const {
      key
    } = event;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
      return;
    }
    event.preventDefault();
    const tabs = Array.from(this.template.querySelectorAll('.tab-navigation button[role="tab"]'));
    if (tabs.length === 0) {
      return;
    }
    const currentIndex = tabs.indexOf(event.currentTarget);
    const lastIndex = tabs.length - 1;
    let targetIndex = currentIndex;
    if (key === 'Home') {
      targetIndex = 0;
    } else if (key === 'End') {
      targetIndex = lastIndex;
    } else if (key === 'ArrowLeft') {
      targetIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
    } else if (key === 'ArrowRight') {
      targetIndex = currentIndex >= lastIndex ? 0 : currentIndex + 1;
    }
    const targetButton = tabs[targetIndex];
    const targetTab = targetButton.dataset.tab;
    if (targetTab) {
      this.switchTo(targetTab);
    }
    targetButton.focus();
  }
  switchTo(tab) {
    if (!tab || tab === this.activeTab) {
      return;
    }
    this._activeTab = tab;

    // Track tab switch analytics
    const analyticsEvent = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        elementType: 'tab',
        eventType: 'click',
        button: {
          callToAction: {
            label: tab,
            name: `competencies_${tab.toLowerCase()}_tab`
          }
        }
      }
    });
    this.dispatchEvent(analyticsEvent);
  }
  handleToggleRow(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;
    const competencyId = button.dataset.id;
    if (!competencyId) {
      return;
    }
    const currentTabExpandedRows = {
      ...(this.expandedRowsByTab[this.activeTab] || {})
    };
    const isNowExpanded = !currentTabExpandedRows[competencyId];
    if (isNowExpanded) {
      currentTabExpandedRows[competencyId] = true;
    } else {
      delete currentTabExpandedRows[competencyId];
    }
    this.expandedRowsByTab = {
      ...this.expandedRowsByTab,
      [this.activeTab]: currentTabExpandedRows
    };
    const action = isNowExpanded ? 'expand' : 'collapse';
    const analyticsEvent = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        elementType: 'button',
        eventType: 'click',
        button: {
          callToAction: {
            label: `${action} competency row`,
            name: `competencies_${this.activeTab.toLowerCase()}_row_${action}`
          }
        }
      }
    });
    this.dispatchEvent(analyticsEvent);
  }
  formatCsat(csat) {
    if (csat === null || csat === undefined || csat === 0) {
      return '-';
    }
    return csat.toFixed(1);
  }
  formatNumber(num) {
    if (num === null || num === undefined || num === 0) {
      return '-';
    }
    return num.toString();
  }
  closeAllTooltips(restoreFocus = false) {
    // Closing removes the tooltip's focused content (the close button, or the
    // tooltip itself after Escape moves focus into it), so focus would fall
    // back to <body>. Return it to the trigger that opened the tooltip.
    const trigger = this.openTooltipTrigger;
    this.openTooltip = null;
    this.openTooltipTrigger = null;
    if (restoreFocus && trigger) {
      // Track the frame like tooltipRafId so disconnectedCallback() can cancel
      // it — navigating right after close would otherwise focus() a detached
      // trigger. The isConnected guard also skips a trigger removed before the
      // frame runs.
      if (this.focusRafId !== null) {
        cancelAnimationFrame(this.focusRafId);
      }
      this.focusRafId = requestAnimationFrame(() => {
        this.focusRafId = null;
        if (trigger.isConnected) {
          trigger.focus();
        }
      });
    }
  }
  toggleTooltip(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;
    const tooltipName = button.dataset.tooltip;
    if (!tooltipName) {
      return;
    }

    // Toggle: if already open, close it; otherwise open it
    if (this.openTooltip === tooltipName) {
      this.openTooltip = null;
      this.openTooltipTrigger = null;
    } else {
      this.openTooltip = tooltipName;
      this.openTooltipTrigger = button;
      // Position tooltip after it's rendered. Route through the same
      // tracked frame as repositionOpenTooltip() so disconnectedCallback()
      // can cancel it; bail if the tooltip was toggled before it fires.
      if (this.tooltipRafId !== null) {
        cancelAnimationFrame(this.tooltipRafId);
      }
      this.tooltipRafId = requestAnimationFrame(() => {
        this.tooltipRafId = null;
        if (this.openTooltip === tooltipName && this.openTooltipTrigger === button) {
          this.positionTooltip(button, tooltipName);
        }
      });
    }
  }
  positionTooltip(trigger, tooltipName) {
    if (!this.template) {
      return;
    }
    const tooltipElement = this.template.querySelector(`[data-testid="tooltip-${tooltipName}"]`);
    if (!tooltipElement) {
      return;
    }
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const padding = 16;
    const arrowHeight = 12; // Arrow height from CSS

    // Position tooltip above the trigger with space for arrow (flush alignment)
    let top = triggerRect.top - tooltipRect.height - arrowHeight;

    // If tooltip would go above viewport, position it below instead
    if (top < padding) {
      top = triggerRect.bottom + arrowHeight;
      tooltipElement.classList.add('tooltip-below');
    } else {
      tooltipElement.classList.remove('tooltip-below');
    }

    // Handle horizontal alignment based on tooltip variant
    // All tooltips start centered on trigger, then get clamped to viewport
    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

    // Ensure tooltip doesn't go off-screen horizontally
    const minLeft = padding;
    const maxLeft = window.innerWidth - tooltipRect.width - padding;
    if (left < minLeft) {
      left = minLeft;
    } else if (left > maxLeft) {
      left = maxLeft;
    }

    // Apply positioning
    tooltipElement.style.top = `${top}px`;
    tooltipElement.style.left = `${left}px`;

    // Position arrow relative to trigger center
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const arrowLeft = triggerCenter - left;

    // Clamp arrow position within tooltip bounds (with some padding for aesthetics)
    const minArrowLeft = 20;
    const maxArrowLeft = tooltipRect.width - 20;
    const clampedArrowLeft = Math.max(minArrowLeft, Math.min(maxArrowLeft, arrowLeft));
    tooltipElement.style.setProperty('--arrow-left', `${clampedArrowLeft}px`);
  }
  closeTooltip(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeAllTooltips(true);
  }
  repositionOpenTooltip() {
    // Coalesce bursts of scroll/resize events into a single per-frame
    // reposition: skip scheduling if a callback is already pending.
    if (!this.openTooltip || !this.openTooltipTrigger || this.tooltipRafId !== null) {
      return;
    }
    this.tooltipRafId = requestAnimationFrame(() => {
      this.tooltipRafId = null;
      if (this.openTooltip && this.openTooltipTrigger) {
        this.positionTooltip(this.openTooltipTrigger, this.openTooltip);
      }
    });
  }
  onResize() {
    this.repositionOpenTooltip();
  }
  onScroll() {
    this.repositionOpenTooltip();
  }

  // Modal methods
  get modalTitle() {
    const certificationsTitle = this.i18n?.listingDetails?.competencies?.modal?.title;
    if (this.modalSubcategoryName) {
      return `${certificationsTitle} - ${this.modalCompetencyName} > ${this.modalSubcategoryName}`;
    }
    return `${certificationsTitle} - ${this.modalCompetencyName}`;
  }
  get modalDescription() {
    return this.i18n?.listingDetails?.competencies?.modal?.description;
  }

  // Card-view "About Competency Metrics" info modal. Card mode (tablet/mobile)
  // visually hides the column-header row and its per-column tooltips, so this
  // consolidated modal is the only way to read what each metric means.
  // The modal header reuses metricsTrigger — the modal title and the trigger
  // label are the same string ("About Competency Metrics").
  get metricsModalTitle() {
    return this.i18n?.listingDetails?.competencies?.metricsTrigger?.label;
  }
  get hasCertificationsData() {
    return this.modalCertifications && this.modalCertifications.length > 0;
  }
  openCertificationsModal(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;
    const competencyId = button.dataset.competencyId;
    const subcategoryName = button.dataset.subcategoryName;
    if (!competencyId) {
      return;
    }
    const competency = this.competencies.find(comp => comp.id === competencyId);
    if (!competency) {
      return;
    }
    this.certificationsModalTrigger = button;

    // Set modal data for category-level
    this.modalCompetencyName = competency.name;
    this.modalSubcategoryName = '';
    this.modalTotalCertifications = competency.numCertifications;

    // Get all certifications from API
    const allCertifications = this.consultantCertifications?.certifications || [];

    // If clicking on a subcategory, filter by specialization
    if (subcategoryName) {
      const spec = competency.specializations.find(s => s.name === subcategoryName);
      if (spec) {
        this.modalSubcategoryName = spec.name;
        this.modalTotalCertifications = spec.numCertifications;
        this.modalCertifications = this.filterCertificationsBySpecialization(allCertifications, competency.name, subcategoryName);
      } else {
        this.modalCertifications = [];
      }
    } else {
      // Competency-level: filter by competency name
      this.modalCertifications = this.filterCertificationsByCompetency(allCertifications, competency.name);
    }

    // Dispatch analytics event
    const analyticsEvent = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        elementType: 'button',
        eventType: 'click',
        button: {
          callToAction: {
            label: subcategoryName ? `View certifications for ${competency.name} - ${subcategoryName}` : `View certifications for ${competency.name}`,
            name: subcategoryName ? `competencies_certifications_modal_open_subcategory` : `competencies_certifications_modal_open_category`
          }
        }
      }
    });
    this.dispatchEvent(analyticsEvent);
    this.showCertificationsModal = true;
  }
  handleCertificationsModalClose() {
    // Reset modal state
    this.showCertificationsModal = false;
    this.modalCompetencyName = '';
    this.modalSubcategoryName = '';
    this.modalCertifications = [];
    this.modalTotalCertifications = 0;
    // Return focus to the certification button that opened the modal so
    // keyboard and screen-reader users are not dropped to <body>.
    this.certificationsModalTrigger?.focus();
    this.certificationsModalTrigger = null;
  }
  openMetricsModal(event) {
    event.preventDefault();
    event.stopPropagation();
    this.metricsModalTrigger = event.currentTarget;
    const analyticsEvent = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        elementType: 'button',
        eventType: 'click',
        button: {
          callToAction: {
            // Reuse the localized modal title so label-based analytics
            // match the control users see in translated experiences.
            label: this.metricsModalTitle,
            name: 'competencies_metrics_modal_open'
          }
        }
      }
    });
    this.dispatchEvent(analyticsEvent);
    this.showMetricsModal = true;
  }
  handleMetricsModalClose() {
    this.showMetricsModal = false;
    // Return focus to the metrics trigger that opened the modal so keyboard
    // and screen-reader users are not dropped to <body>.
    this.metricsModalTrigger?.focus();
    this.metricsModalTrigger = null;
  }

  /**
   * Maps API certifications to display format with filtering
   */
  mapCertifications(allCertifications, filterFn) {
    return allCertifications.filter(filterFn).map(cert => ({
      name: cert.name,
      total: cert.count
    }));
  }

  /**
   * Filter certifications by competency name (matches cloud field)
   * Deduplicates certifications by name since the same certification can be
   * mapped to multiple specializations within a competency.
   */
  filterCertificationsByCompetency(allCertifications, competencyName) {
    const mapped = this.mapCertifications(allCertifications, cert => cert.cloud === competencyName);

    // Deduplicate by name - keep first occurrence (count is same across duplicates)
    return mapped.filter((cert, index, arr) => arr.findIndex(c => c.name === cert.name) === index);
  }

  /**
   * Filter certifications by specialization name
   */
  filterCertificationsBySpecialization(allCertifications, competencyName, specializationName) {
    return this.mapCertifications(allCertifications, cert => cert.cloud === competencyName && cert.specialization_name === specializationName);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(CompetenciesV2, {
  publicProps: {
    listing: {
      config: 3
    }
  },
  track: {
    expandedRowsByTab: 1
  },
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    },
    scrollToParam: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectScrollToQueryParam
        };
      }
    }
  },
  fields: ["_activeTab", "openTooltip", "openTooltipTrigger", "tooltipRafId", "focusRafId", "handleOutsideClick", "handleEscapeKey", "handleResize", "handleScroll", "showCertificationsModal", "showMetricsModal", "certificationsModalTrigger", "metricsModalTrigger", "modalCompetencyName", "modalSubcategoryName", "modalCertifications", "modalTotalCertifications"]
});
const __lwc_component_class_internal = _registerComponent(CompetenciesV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;